<template>
	<div class="rsm">
		<div class="rsm-row">
			<AnualCalendar
				class="rsm-picker"
				:value="selected"
				trigger-variant="range"
				range-trigger-label="Desde"
				placeholder="primer documento"
				format-label-date="MMMM YYYY"
				:disable-newer-dates="true"
				:disabled-from="today"
				:disabled="loading || saving"
				@date-change="onPick"
			/>
			<div class="rsm-text">
				<span class="rsm-title">Primer mes visible del reporte financiero</span>
				<span class="rsm-hint">
					Gráficos, tablas y filtros de la web solo muestran datos desde este mes.
					Vacío = desde el primer documento de la empresa.
				</span>
			</div>
		</div>

		<div v-if="!loading" class="rsm-status">
			<span class="rsm-dot" :class="'rsm-dot--' + tone" aria-hidden="true"></span>
			<span class="rsm-status-label">{{ statusLabel }}</span>
			<span v-if="isDirty" class="rsm-meta">{{ pendingLabel }}</span>
		</div>
		<p v-if="showLedgerHint" class="rsm-note">
			<span class="rsm-dot rsm-dot--warn" aria-hidden="true"></span>
			Esta empresa usa libro mayor: con un inicio distinto de enero, el balance de ese año
			se muestra sin el asiento de apertura.
		</p>

		<div class="rsm-actions">
			<SimpleButton class="act act--save" :disabled="!isDirty || loading || saving" @click="save">
				<span class="act__text">Guardar inicio</span>
			</SimpleButton>
			<SimpleButton class="act" :disabled="!saved || loading || saving" @click="clear">
				<span class="act__text">Volver al primer documento</span>
			</SimpleButton>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import AnualCalendar from "@/components/classification/AnualCalendar";
import SimpleButton from "@/components/modals/SimpleButton";
import { getReportStartMonth, setReportStartMonth } from "@/api/carteraAnalista";
import { extractErrorMessage, notificate } from "@/utils/message";

const ISO_DAY = "YYYY-MM-DD";

// Mes de inicio del Reporte Financiero por empresa (panel del analista, card
// "Reporte"). Reusa el selector de mes del filtro de período del reporte y del
// devengo (AnualCalendar) con el chrome "Desde  <mes>", y la misma anatomía
// que su fila vecina "Precálculo": control + texto, línea de estado, acciones.
// Sin Vuex: lee y escribe directo contra la api. El backend recorta los datos
// de la web a este mes; Phi sigue viendo toda la historia.
export default {
	name: "ReportStartMonthControl",
	components: { AnualCalendar, SimpleButton },
	props: {
		companyId: { type: [Number, String], required: true },
	},
	data() {
		return {
			// Mes elegido (Date, día 1) y último valor guardado en el backend.
			selected: null,
			saved: null,
			usesLedger: false,
			loading: true,
			saving: false,
			// Techo del calendario: el mes en curso. Objeto estable para que el
			// watcher de `disabledFrom` del calendario no se dispare por render.
			today: new Date(),
		};
	},
	computed: {
		isDirty() {
			return this.toIso(this.selected) !== this.toIso(this.saved);
		},
		// La línea de estado dice lo que rige HOY en la web (lo guardado); el
		// mes elegido sin guardar va aparte, como pendiente.
		statusLabel() {
			return this.saved
				? `La web empieza en ${this.monthLabel(this.saved)}`
				: "La web empieza en el primer documento";
		},
		pendingLabel() {
			return this.selected
				? `Sin guardar: ${this.monthLabel(this.selected)}`
				: "Sin guardar: volver al primer documento";
		},
		tone() {
			if (this.isDirty) return "pending";
			return this.saved ? "on" : "off";
		},
		// Pista del balance (DECISIONES §20): con mayor, un inicio distinto de
		// enero deja el balance de ese año sin el asiento de apertura. Se evalúa
		// sobre el mes elegido (aún sin guardar) para avisar antes de guardar.
		showLedgerHint() {
			return this.usesLedger && !!this.selected && this.selected.getMonth() !== 0;
		},
	},
	mounted() {
		this.load();
	},
	methods: {
		monthLabel(value) {
			return moment(value).locale("es").format("MMMM YYYY");
		},
		toIso(value) {
			return value ? moment(value).startOf("month").format(ISO_DAY) : null;
		},
		fromIso(iso) {
			return iso ? moment(iso, ISO_DAY).toDate() : null;
		},
		onPick(date) {
			this.selected = date ? moment(date).startOf("month").toDate() : null;
		},
		applyState(data) {
			this.saved = this.fromIso(data.report_start_month);
			this.selected = this.saved;
			this.usesLedger = !!data.uses_ledger;
		},
		async load() {
			this.loading = true;
			try {
				const { data } = await getReportStartMonth(this.companyId);
				this.applyState(data);
			} catch (error) {
				notificate(extractErrorMessage(error, "No se pudo leer el inicio del reporte."), "is-danger");
			} finally {
				this.loading = false;
			}
		},
		async persist(value) {
			this.saving = true;
			try {
				const { data } = await setReportStartMonth(this.companyId, value);
				this.applyState(data);
				notificate(
					value
						? `Inicio guardado: la web empieza en ${this.monthLabel(this.saved)}.`
						: "Inicio limpiado: la web vuelve a empezar en el primer documento.",
					"is-success",
				);
			} catch (error) {
				notificate(extractErrorMessage(error, "No se pudo guardar el inicio del reporte."), "is-danger");
			} finally {
				this.saving = false;
			}
		},
		save() {
			return this.persist(this.toIso(this.selected));
		},
		clear() {
			return this.persist(null);
		},
	},
};
</script>

<style lang="scss" scoped>
// Misma anatomía y disciplina que ReportFactControl (fila vecina): se apila,
// todo envuelve, sin ancho mínimo forzado dentro de la fila de la ficha.
.rsm {
	display: flex;
	flex-direction: column;
	gap: 10px;
	max-width: 640px;
	min-width: 0;

	// El calendario es un componente del reporte: consume tokens --report-*
	// con fallback. Acá los remapeamos a los del analista para que el trigger
	// y la grilla adopten el tema de la ficha sin tocar el componente.
	--report-bg: var(--ap-paper);
	--report-surface: var(--ap-mist);
	--report-border: var(--ap-line-strong);
	--report-border-soft: var(--ap-teal);
	--report-text: var(--ap-ink);
	--report-text-strong-2: var(--ap-ink-2);
	--report-text-muted: var(--ap-slate);
	--report-text-muted-soft: var(--ap-muted);
	--report-accent-bg: var(--ap-teal-wash);
	--report-accent-border: var(--ap-teal);
	--report-accent-text: var(--ap-teal-deep);
	--report-primary: var(--ap-teal);
	--report-primary-hover: var(--ap-teal-deep);
}
.rsm-row {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 8px 14px;
}
.rsm-picker {
	flex-shrink: 0;
}
// El trigger toma la forma de píldora de las acciones de la ficha.
.rsm-picker :deep(.range_trigger) {
	padding: 8px 16px;
	border-radius: var(--ap-radius-pill);
	font-family: var(--ap-font);
}
.rsm-picker :deep(.range_trigger_dates) {
	font-size: 0.85rem;
	text-transform: capitalize;
}
.rsm-text {
	flex: 1 1 220px;
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	padding-top: 4px;
}
.rsm-title {
	font-size: 0.9rem;
	font-weight: 600;
	color: var(--ap-ink);
}
.rsm-hint,
.rsm-meta {
	font-size: 0.8rem;
	line-height: 1.45;
	color: var(--ap-muted);
}
.rsm-status {
	display: flex;
	align-items: baseline;
	flex-wrap: wrap;
	gap: 2px 8px;
	font-size: 0.85rem;
	color: var(--ap-ink-2);
}
.rsm-status-label {
	font-weight: 500;
}
.rsm-dot {
	align-self: center;
	flex-shrink: 0;
	width: 0.55rem;
	height: 0.55rem;
	border-radius: 50%;
	background: var(--ap-muted);
	&--on { background: var(--ap-teal); }
	&--off { background: var(--ap-line-strong); }
	&--pending { background: var(--ap-warn); }
	&--warn { background: var(--ap-warn); }
}
.rsm-note {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	margin: 0;
	font-size: 0.8rem;
	line-height: 1.45;
	color: var(--ap-warn-ink);

	.rsm-dot {
		align-self: flex-start;
		margin-top: 0.3rem;
	}
}
.rsm-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}
// Guardar es la acción primaria de la fila: acento teal suave mientras haya
// algo pendiente; deshabilitada se apaga con el resto.
.rsm-actions :deep(.simple_button.act--save:not([disabled])) {
	color: var(--ap-teal-deep);
	background: var(--ap-teal-wash);
	border-color: var(--ap-teal);
}
.rsm-actions :deep(.simple_button[disabled]) {
	cursor: default;
	opacity: 0.55;
	transform: none;
}
</style>
